import React, { useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { applyWordFilter } from '../../utils/content';
import { resetPassword } from '../../services/authService';
import * as firestoreService from '../../services/firestoreService';
import { isUserPremium } from '../../utils/premium';
import Avatar from '../common/Avatar';

const UserAdminView = ({ userId }) => {
  const { users, setUsers, posts, reports, currentUser, setView, suspendUser, unsuspendUser, banUser, unbanUser, changeUserRole, wordFilters, showAlert, showConfirm } = useContext(AppContext);
  const [planChanging, setPlanChanging] = useState(false);
  const user = users.find(u => u.id === userId);
  const [selectedPlan, setSelectedPlan] = useState(() => user?.isPremium ? 'caddie_pro' : 'free');
  const userPosts = posts.filter(p => p.userId === userId);
  const userReports = reports.filter(r => r.reportedUserId === userId);
  const actualFollowers = users.filter(u => u.following?.includes(userId)).map(u => u.id);
  const followerCount = actualFollowers.length;

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-black rounded-lg shadow border border-gray-700 p-6 text-center">
          <p className="text-white">User not found</p>
          <button onClick={() => setView('admin-users')} className="mt-4 px-2 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d]">
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  const handleSuspend = () => {
    const reason = prompt('Enter suspension reason:');
    if (reason) {
      const daysInput = prompt('Enter suspension duration in days (leave empty for permanent):');
      const days = daysInput ? parseInt(daysInput) : null;
      suspendUser(userId, reason, days);
      showAlert('User suspended successfully', 'Success');
    }
  };

  const handleUnsuspend = async () => {
    if (await showConfirm(`Are you sure you want to unsuspend ${user.name}?`, 'Unsuspend User', 'Unsuspend')) {
      unsuspendUser(userId);
      showAlert('User unsuspended successfully', 'Success');
    }
  };

  const handleBan = async () => {
    const reason = prompt('Enter ban reason:');
    if (reason) {
      if (await showConfirm(`Are you sure you want to permanently ban ${user.name}?`, 'Ban User', 'Ban', true)) {
        banUser(userId, reason);
        showAlert('User banned successfully', 'Success');
      }
    }
  };

  const handleUnban = async () => {
    if (await showConfirm(`Are you sure you want to unban ${user.name}?`, 'Unban User', 'Unban')) {
      unbanUser(userId);
      showAlert('User unbanned successfully', 'Success');
    }
  };

  const handlePlanSelect = async (newPlan) => {
    if (newPlan === selectedPlan) return;
    const label = newPlan === 'caddie_pro' ? 'Caddie Pro' : 'Free';
    const confirmed = await showConfirm(
      `Change ${user.name}'s plan to ${label}?`,
      'Change Plan',
      'Confirm'
    );
    if (!confirmed) return;
    setPlanChanging(true);
    try {
      const updates = newPlan === 'caddie_pro'
        ? { isPremium: true }
        : { isPremium: false, trialUsed: false, trialStartDate: null, trialEndsAt: null };
      await firestoreService.updateUser(userId, updates);
      setUsers(users.map(u => u.id === userId ? { ...u, ...updates } : u));
      setSelectedPlan(newPlan);
      showAlert(`Plan updated to ${label} for ${user.name}.`, 'Success');
    } catch (err) {
      showAlert('Failed to update plan.', 'Error');
    } finally {
      setPlanChanging(false);
    }
  };

  const handleToggleCoach = async () => {
    const newValue = !user.isCoach;
    setUsers(users.map(u => u.id === userId ? { ...u, isCoach: newValue } : u));
    try {
      await firestoreService.updateUser(userId, { isCoach: newValue });
    } catch (err) {
      // revert on failure
      setUsers(users.map(u => u.id === userId ? { ...u, isCoach: !newValue } : u));
      showAlert('Failed to update coach status.', 'Error');
    }
  };

  const handleRoleChange = async (newRole) => {
    // Only super admins can promote to admin
    if (newRole === 'admin' && currentUser.role !== 'superadmin') {
      showAlert('Only Super Admins can grant Admin permissions.', 'Error');
      return;
    }

    if (await showConfirm(`Are you sure you want to change ${user.name}'s role to ${newRole}?`, 'Change Role', 'Confirm')) {
      changeUserRole(userId, newRole);
      showAlert('User role updated successfully', 'Success');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <button
        onClick={() => setView('admin-users')}
        className="mb-4 text-white hover:text-[#16a34a] flex items-center space-x-2"
      >
        <span>← Back to User Management</span>
      </button>

      <div className="bg-black rounded-lg shadow border border-gray-700 p-6 mb-6">
        <div className="flex items-center space-x-4 mb-6">
          <Avatar user={user} size="xl" />
          <div>
            <h1 className="text-2xl font-bold text-white">{user.name}</h1>
            <p className="text-white">@{user.handle}</p>
            <p className="text-sm text-white">{user.email}</p>
          </div>
        </div>

        {/* Status and Role */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-sm text-white mb-2">Status</p>
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
              user.status === 'active' ? 'bg-green-800 text-pure-white' :
              user.status === 'suspended' ? 'bg-yellow-800 text-pure-white' :
              'bg-red-800 text-pure-white'
            }`}>
              {user.status}
            </span>
          </div>
          <div>
            <p className="text-sm text-white mb-2">Role</p>
            <span className="inline-block px-3 py-1 rounded-full text-sm font-bold bg-[#16a34a] text-pure-white">
              {user.role}
            </span>
          </div>
        </div>

        {/* Suspension/Ban Info */}
        {user.status === 'suspended' && (
          <div className="bg-yellow-900 border border-yellow-700 rounded-lg p-4 mb-6">
            <p className="text-sm font-bold text-white mb-1">Suspended</p>
            <p className="text-sm text-white">Reason: {user.suspensionReason}</p>
            {user.suspensionEndDate && (
              <p className="text-sm text-white">Until: {user.suspensionEndDate === 'permanent' ? 'Permanent' : new Date(user.suspensionEndDate).toLocaleDateString()}</p>
            )}
          </div>
        )}

        {user.status === 'banned' && (
          <div className="bg-red-900 border border-red-700 rounded-lg p-4 mb-6">
            <p className="text-sm font-bold text-white mb-1">Banned</p>
            <p className="text-sm text-white">Reason: {user.banReason}</p>
            {user.banDate && (
              <p className="text-sm text-white">Date: {new Date(user.banDate).toLocaleDateString()}</p>
            )}
          </div>
        )}

        {/* User Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{userPosts.length}</p>
            <p className="text-sm text-white">Posts</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{followerCount}</p>
            <p className="text-sm text-white">Followers</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{user.following?.length || 0}</p>
            <p className="text-sm text-white">Following</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{userReports.length}</p>
            <p className="text-sm text-white">Reports</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          <div>
            <p className="text-sm font-bold text-white mb-2">Account Actions</p>
            <div className="flex flex-wrap gap-2">
              {user.status === 'active' && (
                <button
                  onClick={handleSuspend}
                  className="px-2 py-1 bg-yellow-600 text-pure-white rounded-lg hover:bg-yellow-700"
                >
                  Suspend User
                </button>
              )}
              {user.status === 'suspended' && (
                <button
                  onClick={handleUnsuspend}
                  className="px-2 py-1 bg-green-600 text-pure-white rounded-lg hover:bg-green-700"
                >
                  Unsuspend User
                </button>
              )}
              {user.status !== 'banned' && (
                <button
                  onClick={handleBan}
                  className="px-2 py-1 bg-red-600 text-pure-white rounded-lg hover:bg-red-700"
                >
                  Ban User
                </button>
              )}
              {user.status === 'banned' && (
                <button
                  onClick={handleUnban}
                  className="px-2 py-1 bg-green-600 text-pure-white rounded-lg hover:bg-green-700"
                >
                  Unban User
                </button>
              )}
              <button
                onClick={async () => {
                  if (await showConfirm(`Send a password reset email to ${user.email}?`, 'Reset Password', 'Send')) {
                    try {
                      await resetPassword(user.email);
                      showAlert(`Password reset email sent to ${user.email}`, 'Success');
                    } catch (err) {
                      showAlert('Failed to send password reset email.', 'Error');
                    }
                  }
                }}
                className="px-2 py-1 bg-blue-600 text-pure-white rounded-lg hover:bg-blue-700"
              >
                Reset Password
              </button>
              <button
                onClick={() => setView(`user-profile-${userId}`)}
                className="px-2 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d]"
              >
                View Profile
              </button>
            </div>
          </div>

          <div>
            <p className="text-sm font-bold text-white mb-2">Plan Management</p>
            <div className="flex items-center space-x-3">
              <select
                value={selectedPlan}
                onChange={e => handlePlanSelect(e.target.value)}
                disabled={planChanging}
                className="px-3 py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-[#22c55e] disabled:opacity-50"
              >
                <option value="free">Free</option>
                <option value="caddie_pro">Caddie Pro</option>
              </select>
              {planChanging && <span className="text-xs text-gray-400">Updating...</span>}
            </div>
          </div>

          <div>
            <p className="text-sm font-bold text-white mb-2">Role Management</p>
            <div className="flex flex-wrap gap-2 mb-3">
              <button
                onClick={() => handleRoleChange('user')}
                disabled={user.role === 'user'}
                className={`px-2 py-1 rounded-lg ${user.role === 'user' ? 'bg-gray-700 text-gray-300 cursor-not-allowed' : 'bg-blue-600 text-pure-white hover:bg-blue-700'}`}
              >
                Make User
              </button>
              <button
                onClick={() => handleRoleChange('moderator')}
                disabled={user.role === 'moderator'}
                className={`px-2 py-1 rounded-lg ${user.role === 'moderator' ? 'bg-gray-700 text-gray-300 cursor-not-allowed' : 'bg-blue-600 text-pure-white hover:bg-blue-700'}`}
              >
                Make Moderator
              </button>
              {currentUser.role === 'superadmin' && (
                <button
                  onClick={() => handleRoleChange('admin')}
                  disabled={user.role === 'admin' || user.role === 'superadmin'}
                  className={`px-2 py-1 rounded-lg ${user.role === 'admin' || user.role === 'superadmin' ? 'bg-gray-700 text-gray-300 cursor-not-allowed' : 'bg-blue-600 text-pure-white hover:bg-blue-700'}`}
                >
                  Make Admin
                </button>
              )}
            </div>
            {/* Coach toggle */}
            <label className="flex items-center space-x-2 cursor-pointer w-fit">
              <input
                type="checkbox"
                checked={!!user.isCoach}
                onChange={handleToggleCoach}
                className="w-4 h-4 accent-[#16a34a]"
              />
              <span className="text-sm text-white">Is Coach</span>
              {user.isCoach && (
                <span className="flex items-center text-xs bg-blue-600 text-pure-white px-1.5 py-0.5 rounded-full">
                  Coach
                </span>
              )}
            </label>
          </div>
        </div>
      </div>

      {/* Recent Posts */}
      <div className="bg-black rounded-lg shadow border border-gray-700 p-6 mb-6">
        <h2 className="text-xl font-bold text-white mb-4">Recent Posts ({userPosts.length})</h2>
        {userPosts.length === 0 ? (
          <p className="text-white">No posts yet</p>
        ) : (
          <div className="space-y-4">
            {userPosts.slice(0, 5).map(post => (
              <div key={post.id} className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                <p className="text-sm text-white mb-2">{applyWordFilter(post.content, wordFilters)}</p>
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>{new Date(post.timestamp).toLocaleDateString()}</span>
                  <span>{post.likes?.length || 0} likes • {post.comments?.length || 0} comments</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reports */}
      {userReports.length > 0 && (
        <div className="bg-black rounded-lg shadow border border-gray-700 p-6">
          <h2 className="text-xl font-bold text-white mb-4">Reports Against This User ({userReports.length})</h2>
          <div className="space-y-4">
            {userReports.map(report => (
              <div key={report.id} className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-bold text-white">{report.reason}</p>
                    <p className="text-sm text-white">{report.description}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    report.status === 'pending' ? 'bg-yellow-800 text-pure-white' :
                    report.status === 'resolved' ? 'bg-green-800 text-pure-white' :
                    'bg-gray-700 text-white'
                  }`}>
                    {report.status}
                  </span>
                </div>
                <p className="text-xs text-gray-400">{new Date(report.timestamp).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAdminView;
